import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_CRUISE, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_TOUR, PRODUCT_TYPE_TOUR_V2, PRODUCT_TYPE_ULTRALUX } from 'constants/offer'
import { sortBy } from 'lib/arrayUtils'
import config from 'constants/config'

import request from 'api/requestUtils'
import { isTaxExclusiveForRegion } from 'lib/isTaxExclusive'
import GetClosestAirport from 'lib/getClosestAirport'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { EXPERIENCE_OFFER_TYPE_CHANNEL_MANAGER, EXPERIENCE_OFFER_TYPE_FLASH } from 'constants/experience'
import { EXPERIENCE_OFFER_TYPE_ALWAYS_ON } from 'constants/experience'

export const ORIGIN_FRAGMENT = `
fragment OriginAirportPortParts on AirportPort {
  code
  name
  latitude
  longitude
}
`

export const AIRPORT_FRAGMENT = `
fragment AirportPortParts on AirportPort {
  code
  name
}
`

export const PHONE_NUMBER_FRAGMENT = `
fragment PhoneNumberParts on PhoneNumber {
  humanReadable
  number
}
`

const GeoQuery = () => `
${config.FLIGHT_ENABLED ? AIRPORT_FRAGMENT : ''}
${config.FLIGHT_ENABLED ? ORIGIN_FRAGMENT : ''}
${PHONE_NUMBER_FRAGMENT}

query GetGeoData(
  $regionCode:String!
  $brand:String!
) {
  region(code:$regionCode, brand:$brand) {
    code
    name
    currencyCode
    hasFlights
    insuranceProductName,
    localPhone:phoneNumber(route:LOCAL) {
      ...PhoneNumberParts
    }
    internationalPhone:phoneNumber(route:INTERNATIONAL) {
      ...PhoneNumberParts
    }
    defaultPhone:phoneNumber(route:DEFAULT) {
      ...PhoneNumberParts
    }
  }
  priorityPhoneNumbers(brand:$brand) {
    code
    name
    localPhone:phoneNumber(route:LOCAL) {
      ...PhoneNumberParts
    }
    internationalPhone:phoneNumber(route:INTERNATIONAL) {
      ...PhoneNumberParts
    }
    defaultPhone:phoneNumber(route:DEFAULT) {
      ...PhoneNumberParts
    }
  }
  partnerships(regionCode:$regionCode, brand:$brand) {
    bonusUnit
    brandName
    code
    color
    confirmationText
    currencyCodes
    hasBurn
    hasEarn
    icon {
      id
    }
    iconReversed {
      id
    }
    joinUrl
    landingPage
    landingPageLogo {
      id
    }
    numberMaxLength
    prefix
    programLogo {
      id
    }
    programName
    regionCodes
    reverseLogo {
      id
    }
    rewardConversion
    rewardCurrency
    rewardEarn
    rewardName
    rewardPer
    rewardProgramName
    userFields {
      name
      label
    }
  }
  ${config.FLIGHT_ENABLED ? `
  flights(regionCode:$regionCode, brand:$brand) {
    mainAirport:mainPort {
      ...AirportPortParts
    }
    originAirports:originPorts {
      ...OriginAirportPortParts
    }
  }
  ` : ''}
}
`

interface GraphqlNumberPart {
  humanReadable: string;
  number: string;
}

interface PriorityPhoneNumber {
  localPhone: GraphqlNumberPart;
  defaultPhone: GraphqlNumberPart;
  internationalPhone: GraphqlNumberPart;
  code: string;
  name: string;
}

interface GraphqlRegion {
  code: string;
  name: string;
  currencyCode: string;
  hasFlights: boolean;
  insuranceProductName: string;
  localPhone: GraphqlNumberPart;
  internationalPhone: GraphqlNumberPart;
  defaultPhone: GraphqlNumberPart;
}

interface GraphqlGeoData {
  region: GraphqlRegion;
  flights: {
    closestAirport: App.Airport;
    mainAirport: App.Airport;
    originAirports: Array<App.Airport>;
    destinationAirports: Array<App.Airport>;
    airports: Array<App.Airport>;
  }
  partnerships: Array<App.Partnership>;
  priorityPhoneNumbers: Array<PriorityPhoneNumber>
}

/**
  * Logic to determine the global partnership
  */
function mapPartnership(partnershipList: App.Partnership[]): App.Partnership | null {
  const partnerships = config.VIRGIN_VELOCITY_ENABLED ?
    partnershipList :
    partnershipList.filter(partnership => partnership.code !== VIRGIN_VELOCITY_PARTNERSHIP_CODE)

  if (partnerships.length === 0) {
    return null
  }

  const velocityPartnership = partnerships.find(partnership => partnership.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE)
  return velocityPartnership ?
      {
        ...velocityPartnership,
        availableForProductType: [
          OFFER_TYPE_HOTEL,
          OFFER_TYPE_ALWAYS_ON,
          OFFER_TYPE_LAST_MINUTE,
          OFFER_TYPE_CRUISE,
          EXPERIENCE_OFFER_TYPE_ALWAYS_ON,
          EXPERIENCE_OFFER_TYPE_FLASH,
          EXPERIENCE_OFFER_TYPE_CHANNEL_MANAGER,
          PRODUCT_TYPE_TOUR_V2,
          PRODUCT_TYPE_ULTRALUX,
        ],
      } :
      {
        ...partnerships[0], /** Assumption (copied from old code): There is only ever a single global partnership at one time */
        availableForOfferType: [OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR, OFFER_TYPE_ALWAYS_ON],
      }
}

function mapGeoData(
  data: GraphqlGeoData,
  latitude?: number,
  longitude?: number,
): Partial<App.GeoState> {
  const partnership = mapPartnership(data.partnerships)
  const priorityPhoneNumber = data.priorityPhoneNumbers.find(p => p.code === data.region.code)
  const geoData: Partial<App.GeoState> = {
    originAirports: sortBy<App.Airport, string>(data.flights?.originAirports ?? [], a => a.name, 'asc'),
    mainAirport: data.flights?.mainAirport,
    closestAirport: GetClosestAirport(data.flights?.originAirports, latitude, longitude) ?? data.flights?.mainAirport,
    ...(partnership && { partnership }),
    currentRegionHasFlights: data.region.hasFlights && config.FLIGHT_ENABLED,
    currentRegionName: data.region.name,
    currentRegionCode: data.region.code,
    currentCurrency: data.region.currencyCode,
    currentContact: {
      local: data.region.localPhone,
      international: data.region.internationalPhone,
      default: data.region.defaultPhone === data.region.internationalPhone ? 'international' : 'local',
    },
    insuranceProductName: data.region.insuranceProductName,
    flightsSupportEmail: 'flights@luxuryescapes.com',
    currentPriorityContact: priorityPhoneNumber ? {
      local: priorityPhoneNumber.localPhone,
      international: priorityPhoneNumber.internationalPhone,
      default: priorityPhoneNumber.defaultPhone === priorityPhoneNumber.internationalPhone ? 'international' : 'local',
    } : undefined,
    showTaxExclusivePrices: isTaxExclusiveForRegion(data.region.code),
  }

  return geoData
}

export async function getGeoData(
  regionCode: string,
  latitude?: number,
  longitude?: number,
): Promise<Partial<App.GeoState>> {
  const geo = await request.graphql<App.ApiResponse<any>>({
    query: GeoQuery(),
    variables: {
      regionCode,
      brand: config.BRAND,
    },
  })

  if (!geo.data.region) {
    throw geo.data.errors || new Error('Missing region data from Geo')
  }

  return mapGeoData(geo.data, latitude, longitude)
}
